import { sortedContacts, activeContactSignal, isLoadingContactsSignal, contactsErrorSignal, loadContacts } from '../../state/contacts.store';
import type { Contact } from '../../types/contact';
import { isAddContactModalOpenSignal, isGeminiModalOpenSignal } from '../../state/ui.store';
import { isKanbanOpenSignal } from '../../state/kanban.store';
import { logout } from '../../state/auth.store';
import { ContactCard } from './ContactCard';
import { ContactSearch } from './ContactSearch';
import { ContactSkeleton } from './ContactSkeleton';
import { Plus, ShieldCheck, Settings, LogOut, Kanban, RefreshCw, AlertCircle } from 'lucide-preact';

interface ContactSidebarProps {
  onContextMenu?: (e: MouseEvent, contact: Contact) => void;
  onLongPress?: (e: TouchEvent, contact: Contact) => void;
  onRequestDelete?: (contact: Contact) => void;
}

export function ContactSidebar({ onContextMenu, onLongPress, onRequestDelete }: ContactSidebarProps) {
  const contacts = sortedContacts.value;

  return (
    <aside class="app-sidebar">
      {/* Header Sidebar */}
      <div class="p-4 border-b border-slate-800 flex items-center justify-between">
        <div class="flex items-center gap-3">
          <div class="w-8 h-8 flex items-center justify-center flex-shrink-0">
            <img src="/logo.png" alt="Alfred AI Logo" class="w-full h-full object-contain drop-shadow-[0_0_8px_rgba(6,182,212,0.4)] scale-110" />
          </div>
          <div>
            <h2 class="text-sm font-bold text-slate-100">Alfred AI</h2>
            <p class="text-[10px] text-slate-400 font-mono">Monitoramento Inteligente</p>
          </div>
        </div>
        <button
          onClick={() => isAddContactModalOpenSignal.value = true}
          class="w-9 h-9 flex items-center justify-center bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl transition shadow-md shadow-cyan-600/20 cursor-pointer"
          title="Monitorar Novo Contato"
        >
          <Plus class="w-5 h-5" />
        </button>
      </div>

      {/* Botão Módulo Kanban de Tarefas */}
      <div class="px-3 pt-3 pb-1">
        <button
          onClick={() => isKanbanOpenSignal.value = true}
          class="w-full py-2 px-3 bg-gradient-to-r from-cyan-600/20 via-indigo-600/20 to-slate-900 hover:from-cyan-600/30 hover:to-indigo-600/30 border border-cyan-500/40 hover:border-cyan-400/60 rounded-xl text-cyan-300 hover:text-cyan-200 font-extrabold text-xs transition flex items-center justify-center gap-2 cursor-pointer shadow-lg group"
        >
          <Kanban class="w-4 h-4 text-cyan-400 group-hover:scale-110 transition-transform" />
          <span>Quadro Kanban de Tarefas</span>
        </button>
      </div>

      {/* Search Input */}
      <ContactSearch />

      {/* Contacts List */}
      <div class="flex-1 overflow-y-auto overflow-x-hidden p-2 space-y-1" id="contacts-list">
        {isLoadingContactsSignal.value && contacts.length === 0 ? (
          <ContactSkeleton />
        ) : contactsErrorSignal.value && contacts.length === 0 ? (
          <div class="p-6 text-center flex flex-col items-center justify-center">
            <AlertCircle class="w-8 h-8 text-rose-400 mb-2 opacity-80" />
            <p class="text-xs text-rose-300 font-medium mb-3 max-w-[200px] leading-relaxed">
              {contactsErrorSignal.value}
            </p>
            <button
              onClick={() => loadContacts()}
              class="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-100 text-xs font-semibold rounded-xl border border-slate-700 transition flex items-center gap-1.5 cursor-pointer shadow-sm active:scale-95"
            >
              <RefreshCw class="w-3.5 h-3.5 text-cyan-400" />
              <span>Tentar novamente</span>
            </button>
          </div>
        ) : contacts.length === 0 ? (
          <div class="p-6 text-center text-xs font-medium text-slate-400">
            Nenhum contato monitorado.
          </div>
        ) : (
          contacts.map(c => (
            <ContactCard
              key={c.id}
              contact={c}
              onContextMenu={onContextMenu}
              onLongPress={onLongPress}
              onRequestDelete={onRequestDelete}
            />
          ))
        )}
      </div>

      {/* Footer Settings & Logout */}
      <div class="p-3.5 border-t border-slate-800 flex items-center justify-between text-xs text-slate-300 bg-slate-950/40">
        <span class="flex items-center gap-2 text-slate-200 font-semibold">
          <ShieldCheck class="w-4 h-4 text-emerald-400" />
          <span>Admin Conectado</span>
        </span>
        <div class="flex items-center gap-1">
          <button
            onClick={() => isGeminiModalOpenSignal.value = true}
            class="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:bg-slate-800 rounded-lg transition cursor-pointer"
            title="Configurações da Chave de API"
          >
            <Settings class="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              activeContactSignal.value = null;
              logout();
            }}
            class="w-9 h-9 flex items-center justify-center text-slate-400 hover:text-rose-400 hover:bg-slate-800 rounded-lg transition cursor-pointer"
            title="Sair"
          >
            <LogOut class="w-4 h-4" />
          </button>
        </div>
      </div>
    </aside>
  );
}
